/**
 * Permission keys exactly as `POST /auth/login` returns them in `permissions`.
 *
 * The full vocabulary is also served by `GET /roles/permissions`, which the role editor lists as
 * its checklist — so these are the same keys a role is built from.
 *
 * These are the authority on what a user may see: the sidebar and the route guards both read
 * them. A `_VIEW` key grants read access to a module and a `_MANAGE` key grants write on top of
 * it, so a module gate accepts either while a write action demands the `_MANAGE` key. A few
 * modules are write-only by nature (projects, sync) and ship just the one key. `PLATFORM_ADMIN`
 * is the platform-owner catch-all and gates no screen of its own.
 */
export const PERMISSION = {
  CLIENTS_VIEW: 'CLIENTS_VIEW',
  CLIENTS_MANAGE: 'CLIENTS_MANAGE',
  USERS_VIEW: 'USERS_VIEW',
  USERS_MANAGE: 'USERS_MANAGE',
  BILLING_VIEW: 'BILLING_VIEW',
  BILLING_MANAGE: 'BILLING_MANAGE',
  TICKETS_VIEW: 'TICKETS_VIEW',
  TICKETS_MANAGE: 'TICKETS_MANAGE',
  EVENT_LOGS_VIEW: 'EVENT_LOGS_VIEW',
  INTEGRATIONS_VIEW: 'INTEGRATIONS_VIEW',
  INTEGRATIONS_MANAGE: 'INTEGRATIONS_MANAGE',
  SYNC_MANAGE: 'SYNC_MANAGE',
  PROJECTS_MANAGE: 'PROJECTS_MANAGE',
  ROLES_VIEW: 'ROLES_VIEW',
  ROLES_MANAGE: 'ROLES_MANAGE',
  PLATFORM_ADMIN: 'PLATFORM_ADMIN',
} as const;

export type Permission = (typeof PERMISSION)[keyof typeof PERMISSION];

/** The module names the app gates on, kept as their own vocabulary from the permission keys. */
export const MODULE = {
  CLIENT: 'CLIENT',
  USER: 'USER',
  INTEGRATION: 'INTEGRATION',
  EVENT_LOG: 'EVENT_LOG',
  SUPPORT: 'SUPPORT',
  SUBSCRIPTION: 'SUBSCRIPTION',
  PROJECT: 'PROJECT',
  SYNC: 'SYNC',
  ROLE: 'ROLE',
} as const;

/**
 * Which permissions unlock each module — holding **any one** of them is enough.
 *
 * This is the single source for both axes: `AuthContext` derives the user's enabled modules from
 * it, and the menu items and route guards quote it as their `requiredPermissions`, so the two can
 * never disagree about what a module needs.
 */
export const MODULE_PERMISSIONS: Record<string, string[]> = {
  [MODULE.CLIENT]: [PERMISSION.CLIENTS_VIEW, PERMISSION.CLIENTS_MANAGE],
  [MODULE.USER]: [PERMISSION.USERS_VIEW, PERMISSION.USERS_MANAGE],
  [MODULE.INTEGRATION]: [PERMISSION.INTEGRATIONS_VIEW, PERMISSION.INTEGRATIONS_MANAGE],
  [MODULE.EVENT_LOG]: [PERMISSION.EVENT_LOGS_VIEW],
  [MODULE.SUPPORT]: [PERMISSION.TICKETS_VIEW, PERMISSION.TICKETS_MANAGE],
  [MODULE.SUBSCRIPTION]: [PERMISSION.BILLING_VIEW, PERMISSION.BILLING_MANAGE],
  [MODULE.PROJECT]: [PERMISSION.PROJECTS_MANAGE],
  [MODULE.SYNC]: [PERMISSION.SYNC_MANAGE],
  [MODULE.ROLE]: [PERMISSION.ROLES_VIEW, PERMISSION.ROLES_MANAGE],
};

/**
 * What it takes to *write* in each module, as opposed to merely reaching it. Screens gate their
 * Add / Edit / Delete controls on this, so a user holding only the `_VIEW` key sees the list and
 * none of the actions.
 */
export const MODULE_MANAGE_PERMISSIONS: Record<string, string> = {
  [MODULE.CLIENT]: PERMISSION.CLIENTS_MANAGE,
  [MODULE.USER]: PERMISSION.USERS_MANAGE,
  [MODULE.INTEGRATION]: PERMISSION.INTEGRATIONS_MANAGE,
  [MODULE.SUPPORT]: PERMISSION.TICKETS_MANAGE,
  [MODULE.SUBSCRIPTION]: PERMISSION.BILLING_MANAGE,
  [MODULE.PROJECT]: PERMISSION.PROJECTS_MANAGE,
  [MODULE.SYNC]: PERMISSION.SYNC_MANAGE,
  [MODULE.ROLE]: PERMISSION.ROLES_MANAGE,
};

/** Every module the holder of `permissions` can reach. */
export function modulesForPermissions(permissions: string[]): string[] {
  const held = new Set(permissions);
  return Object.keys(MODULE_PERMISSIONS).filter((module) =>
    MODULE_PERMISSIONS[module].some((permission) => held.has(permission)),
  );
}

/** The permission a module's write actions demand, or `undefined` for a read-only module. */
export function managePermissionFor(module: string): string | undefined {
  return MODULE_MANAGE_PERMISSIONS[module];
}
